import { useRef, useState } from "react";
import { Paperclip } from "lucide-react";
import {
  ATTACHMENT_MAX_SIZE,
  validateAttachment,
  type AttachmentValidationFailure,
} from "@atgis/db";
import { Button } from "../components/button";
import { formatBytes } from "./format";

function errorMessage(err: AttachmentValidationFailure): string {
  switch (err.kind) {
    case "empty":
      return "Boş dosya yüklenemez.";
    case "too_large":
      return `Dosya çok büyük: ${formatBytes(err.size)} (üst sınır ${formatBytes(err.max)})`;
    case "mime_not_allowed":
      return `Bu dosya türüne izin verilmiyor: ${err.mime}`;
  }
}

type Props = {
  caps: { can_add_attachment: boolean };
  onUpload: (file: File) => Promise<void>;
  onError: (message: string) => void;
  acceptHint?: string;
};

export function AttachmentUploader({
  caps,
  onUpload,
  onError,
  acceptHint = `Resim, PDF, DWG, DXF, KML, GeoJSON, ZIP… (≤ ${formatBytes(ATTACHMENT_MAX_SIZE)})`,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  if (!caps.can_add_attachment) return null;

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const err = validateAttachment(file);
    if (err !== null) {
      onError(errorMessage(err));
      return;
    }

    setBusy(true);
    try {
      await onUpload(file);
    } catch (uploadErr) {
      const msg = uploadErr instanceof Error ? uploadErr.message : "Yükleme başarısız";
      onError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={handleChange}
        data-testid="attachment-uploader-input"
      />
      <Button
        size="sm"
        variant="outline"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid="attachment-uploader-button"
      >
        <Paperclip className="h-3 w-3 mr-1" />
        {busy ? "Yükleniyor…" : "Dosya ekle"}
      </Button>
      <span className="text-[10px] text-muted-foreground">{acceptHint}</span>
    </div>
  );
}
